import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { queryClient } from './lib/queryClient';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { HomePage } from './pages/HomePage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { EmailVerificationPage } from './pages/EmailVerificationPage';
import { AccountConfirmedPage } from './pages/AccountConfirmedPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { CompanySettingsPage } from './pages/CompanySettingsPage';
import { EditCompanyPage } from './pages/EditCompanyPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { GroupDetailPage } from './pages/GroupDetailPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { MyTasksPage } from './pages/MyTasksPage';
import { ArchivedDocumentsPage } from './pages/ArchivedDocumentsPage';
import { LatestVersionsPage } from './pages/LatestVersionsPage';
import { DocumentAccessPage } from './pages/DocumentAccessPage';
import { FilteredDocumentsPage } from './pages/FilteredDocumentsPage';
import { LegalNoticePage } from './pages/LegalNoticePage';
import { TermsPage } from './pages/TermsPage';
import { LegalDocumentsPage } from './pages/LegalDocumentsPage';
import { DocumentVersionsPage } from './pages/DocumentVersionsPage';
import { ProtectedRoute } from './components/ProtectedRoute';

function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/email-verification" element={<EmailVerificationPage />} />
          <Route path="/account-confirmed" element={<AccountConfirmedPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/mentions-legales" element={<LegalNoticePage />} />
          <Route path="/cgvu" element={<TermsPage />} />
          <Route
            path="/d/:token"
            element={
              <ProtectedRoute requireCompany={false}>
                <DocumentAccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/legal-documents"
            element={
              <ProtectedRoute requireCompany={false}>
                <LegalDocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-settings"
            element={
              <ProtectedRoute requireCompany={false}>
                <CompanySettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/:companyId/settings"
            element={
              <ProtectedRoute>
                <EditCompanyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/groups/:groupId"
            element={
              <ProtectedRoute>
                <GroupDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <MyTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/archived"
            element={
              <ProtectedRoute>
                <ArchivedDocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/latest"
            element={
              <ProtectedRoute>
                <LatestVersionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/versions/:documentId"
            element={
              <ProtectedRoute>
                <DocumentVersionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/:folderId"
            element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/filtered/:filter"
            element={
              <ProtectedRoute>
                <FilteredDocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
